import { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getStatuses, createStatus, updateStatus, deleteStatus } from '../services/api';

export const StatusesManager = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    status_name: '',
    status_color: '#3B82F6',
    display_order: 1,
  });
  const toast = useToast();

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const data = await getStatuses();
      setStatuses(data);
    } catch (error) {
      toast.error(t('failedToLoadStatuses'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (status = null) => {
    if (status) {
      setEditingStatus(status);
      setFormData({
        status_name: status.status_name,
        status_color: status.status_color,
        display_order: status.display_order,
      });
    } else {
      setEditingStatus(null);
      setFormData({
        status_name: '',
        status_color: '#3B82F6',
        display_order: statuses.length + 1,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingStatus(null);
    setFormData({
      status_name: '',
      status_color: '#3B82F6',
      display_order: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingStatus) {
        await updateStatus(editingStatus.id, formData);
        toast.success(t('statusUpdated'));
      } else {
        await createStatus(formData);
        toast.success(t('statusCreated'));
      }
      handleCloseModal();
      fetchStatuses();
    } catch (error) {
      toast.error(t('failedToSaveStatus'));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t('deleteStatusConfirm'))) {
      return;
    }

    try {
      await deleteStatus(id);
      toast.success(t('statusDeleted'));
      fetchStatuses();
    } catch (error) {
      toast.error(t('statusDeleteFailed'));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('leadStatuses')}</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {t('manageStatuses')}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto">
          <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M12 4v16m8-8H4" />
          </svg>
          {t('addStatus')}
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {statuses.map((status) => (
          <div key={status.id} className="bg-white shadow-md rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-sm font-semibold text-gray-500">#{status.display_order}</span>
                <div
                  className="w-8 h-8 rounded border border-gray-300"
                  style={{ backgroundColor: status.status_color }}
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{status.status_name}</h3>
                  <p className="text-xs text-gray-500">{status.status_color}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                status.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {status.is_active ? t('active') : t('inactive')}
              </span>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <button
                onClick={() => handleOpenModal(status)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title={t('edit') || 'Edit'}
              >
                <Pencil className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleDelete(status.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t('delete') || 'Delete'}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('order')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('statusName')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('color')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('active')}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {statuses.map((status) => (
              <tr key={status.id}>
                <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  {status.display_order}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <div className="text-sm font-medium text-gray-900">
                    {status.status_name}
                  </div>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center gap-2 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: status.status_color }}
                    />
                    <span className="text-sm text-gray-600">{status.status_color}</span>
                  </div>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    status.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {status.is_active ? t('active') : t('inactive')}
                  </span>
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center gap-2 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                    <button
                      onClick={() => handleOpenModal(status)}
                      className="text-blue-600 hover:text-blue-900"
                      title={t('edit') || 'Edit'}
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(status.id)}
                      className="text-red-600 hover:text-red-900"
                      title={t('delete') || 'Delete'}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingStatus ? t('editStatus') : t('createStatus')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('statusName')}
            name="status_name"
            value={formData.status_name}
            onChange={handleChange}
            placeholder={t('statusNameExample')}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('color')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="status_color"
                value={formData.status_color}
                onChange={handleChange}
                className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
              />
              <Input
                name="status_color"
                value={formData.status_color}
                onChange={handleChange}
                placeholder={t('placeholderColor') || '#3B82F6'}
                className="flex-1"
              />
            </div>
          </div>

          <Input
            label={t('displayOrder')}
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            min="1"
            required
          />

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingStatus ? t('updateStatus') : t('createStatus')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCloseModal} className="flex-1">
              {t('cancel')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
